import _ from 'lodash';

import {
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  LoanChangesHistoryState,
} from 'constants/loanApplication';
import { getPendingChangeAuthorKey } from 'utils/pendingChangesAuthors';

import {
  setLoanSavingRelatedDataAction,
  updateLoanChangesHistoryAction,
} from 'store/loanApplications/actions';
import { changePendingChangesAuthorsSelectionAction } from 'store/loanApplications/actions/pendingChangesAuthorsSelection.actions';

import {
  selectIsCancellingLoanApplicationChanges,
  selectIsSavingLoanApplicationChanges,
} from '../selectors';
import {
  selectActivePendingChangesAuthorsApplicationId,
  selectCurrentUserIdSafe,
  selectIsCurrentUserPendingChangesAuthorSelected,
  selectIsPendingChangesByUserEnabled,
  selectSelectedPendingChangesAuthorKeys,
} from '../selectors/pendingChangesAuthorsSelection.selectors';

const checkIsCurrentUserWrite = (historyIncrement, currentUserId) =>
  _.isArray(historyIncrement) &&
  historyIncrement.some(
    entry =>
      entry.historyState !== LoanChangesHistoryState.CANCELLED &&
      getPendingChangeAuthorKey(entry) === currentUserId,
  );

// Editing while "Me" is unticked would hide the user's own new change, so "Me" is ticked again.
const reselectCurrentUserOnHiddenWrite = ({ state, dispatch, historyIncrement }) => {
  const applicationId = selectActivePendingChangesAuthorsApplicationId(state);
  const currentUserId = selectCurrentUserIdSafe(state);

  const isHiddenWrite =
    selectIsPendingChangesByUserEnabled(state) &&
    !_.isNil(applicationId) &&
    !selectIsCurrentUserPendingChangesAuthorSelected(state) &&
    checkIsCurrentUserWrite(historyIncrement, currentUserId);

  if (!isHiddenWrite) {
    return;
  }

  dispatch(
    changePendingChangesAuthorsSelectionAction({
      applicationId,
      authorKeys: [...selectSelectedPendingChangesAuthorKeys(state), currentUserId],
    }),
  );
};

const loanApplicationSingleSaveMiddleware =
  ({ getState, dispatch }) =>
  next =>
  action => {
    const result = next(action);
    const isLoanApplicationActionType = action.type?.startsWith(
      `@${LOAN_APPLICATION_ACTION_NAME_PREFIX}`,
    );

    if (!isLoanApplicationActionType) {
      return result;
    }

    const isPayloadWithoutMetadata =
      _.isNil(action.payload?.metadata) && _.isNil(action.payload?.editorMetadata);

    if (isPayloadWithoutMetadata) {
      return result;
    }

    const state = getState();
    const isSavingLoanChanges = selectIsSavingLoanApplicationChanges(state);
    const isCancellingLoanChanges = selectIsCancellingLoanApplicationChanges(state);

    const { hasPendingChanges, hasPendingChangesByCurrentUser, historyIncrement } =
      action.payload?.metadata || action.payload?.editorMetadata || {};

    const isLoanSavingRelatedDataActionEnabled = [
      hasPendingChanges,
      hasPendingChangesByCurrentUser,
    ].every(_.isBoolean);

    const isUpdateLoanChangesHistoryActionEnabled = _.isObject(historyIncrement);

    const isPendingLoanChangesProcessing = isSavingLoanChanges || isCancellingLoanChanges;

    if (isLoanSavingRelatedDataActionEnabled) {
      next(
        setLoanSavingRelatedDataAction({
          metadata: action.payload?.metadata || action.payload?.editorMetadata,
        }),
      );
    }

    if (isUpdateLoanChangesHistoryActionEnabled) {
      next(
        updateLoanChangesHistoryAction(
          { metadata: action.payload?.metadata || action.payload?.editorMetadata },
          isPendingLoanChangesProcessing,
        ),
      );
    }

    if (!isPendingLoanChangesProcessing) {
      reselectCurrentUserOnHiddenWrite({ state, dispatch, historyIncrement });
    }

    return result;
  };

export default loanApplicationSingleSaveMiddleware;
